import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { seriesToArray, mapAffectedVehicle } from '@/containers/CampaignDetail/AffectedVehicles/SeriesPanel/SeriesRow/series-row-utils';
import { mapAggregateModelSelections } from '@/containers/CampaignDetail/AffectedVehicles/Volume/VolumeDetails/volumeDetails-utils';
import {
  validateClassificationForm, validateRecipientsForm, validateTranslationsForm, validateAffectedVehiclesForm,
  validateSoftwareForm, validateDescriptionForm,
} from '@/helpers/campaignValidations';
import { errorTypes, SOFTWARE, statusCampaign } from '@/helpers/constants';
import { isVansBeforeFlowRemoval } from '@/helpers/utils';
import {
  setCampaignClassification, setCampaignRecipients, setTranslations, setCampaignVehicles, setCampaignHelp, setValidations,
  setCampaignHardware, setCampaignSoftware, setLimitationPlants, setCampaignDescription,
} from '@/redux/Campaign/campaign.action';
import { uniqueSeriesPlants } from '@/redux/Campaign/campaign.utils';
import campaignServices from '@/services/campaignServices';
import useToastContext from './useToastContext';

export const getAttribute = (form) => {
  switch (form) {
    case 'affected-hardware':
      return 'affectedHardware';
    case 'affected-software':
      return SOFTWARE.AFFECTED_SOFTWARE;
    case 'affected-vehicles':
      return 'affectedVehicles';
    default:
      return form;
  }
};

const useCampaignValidate = () => {
  const { addToast } = useToastContext();
  const { t } = useTranslation();
  const { tab } = useParams();

  const dispatch = useDispatch();
  const { id, campaign, version, campaignInfo, validations, timeZone, headerPlants, campaignHelp, originalValues, readOnly, isVANCR } = useSelector((state) => ({
    id: state.campaignReducer?.info?.id,
    version: state.campaignReducer?.info?.version,
    campaignInfo: state.campaignReducer?.info,
    campaign: state.campaignReducer,
    timeZone: state.userReducer?.settings?.timezone,
    originalValues: state.campaignReducer?.originalValues,
    validations: state.campaignReducer?.validations,
    headerPlants: uniqueSeriesPlants(state.campaignReducer?.affectedVehicles?.series),
    campaignHelp: state.campaignReducer?.help,
    readOnly: state.campaignReducer?.info?.authorization?.readOnly,
    isVANCR: isVansBeforeFlowRemoval(state.campaignReducer?.info?.type, state.campaignReducer?.info?.creationDate),
  }));

  let newStateCampaign = { ...campaign };

  const hasAffectedVehiclesToCheck = () => {
    const currentAffectedVehicles = campaign?.affectedVehicles;

    if (!currentAffectedVehicles?.id || !Array.isArray(currentAffectedVehicles?.volumes)) {
      return false;
    }

    if (currentAffectedVehicles?.typeOfAffectedPlantsSelection === 'PLANTS') {
      return Array.isArray(currentAffectedVehicles?.affectedPlants);
    }

    return Array.isArray(currentAffectedVehicles?.series);
  };

  const validateAffectedVehicles = async () => {
    if (hasAffectedVehiclesToCheck()) {
      newStateCampaign = { ...newStateCampaign, affectedVehicles: campaign?.affectedVehicles, limitationPlants: campaign?.limitationPlants };

      return validateAffectedVehiclesForm(newStateCampaign, headerPlants, campaignInfo?.defaultLanguage);
    }
    const response = await Promise.all([
      campaignServices.getCampaignRetry(id, 'affected-vehicles', version),
      campaignServices.getCampaignRetry(id, 'limitation-volumes', version),
    ]);

    const [affVehicles, limitationVolumes] = response;

    const newState = {
      ...mapAffectedVehicle(affVehicles),
      series: seriesToArray(affVehicles?.affectedSeries),
      volumes: mapAggregateModelSelections(limitationVolumes?.volumes, timeZone),
    };
    const plants = uniqueSeriesPlants(newState.series);
    dispatch(setCampaignVehicles(newState));
    const limitationPlants = {};

    if (plants?.length > 0) {
      affVehicles.affectedPlants?.forEach((plant) => {
        limitationPlants[plant.wkzNumber] = plant;
      });
      dispatch(setLimitationPlants(limitationPlants));
    }
    newStateCampaign = { ...newStateCampaign, affectedVehicles: newState, limitationPlants };

    return validateAffectedVehiclesForm(newStateCampaign, plants, campaignInfo?.defaultLanguage);
  };

  const validateForms = (form, data, languageCode, hasToSet) => {
    switch (form) {
      case 'description':
        if (hasToSet) dispatch(setCampaignDescription(data.description));

        return validateDescriptionForm(validations?.[form]?.[languageCode], data, languageCode);
      case 'classification':
        if (hasToSet) dispatch(setCampaignClassification(data.classification));

        return validateClassificationForm(validations?.[form]?.[languageCode], data, languageCode);
      case 'affected-hardware':
        if (hasToSet) dispatch(setCampaignHardware(data?.affectedHardware));

        return { EN: { invalid: false }, DE: { invalid: false } };
      case 'affected-software':
        if (hasToSet) dispatch(setCampaignSoftware(data.affectedSoftware));

        return validateSoftwareForm(validations?.[form]?.[languageCode], data, languageCode, isVANCR);
      case 'recipients':

        if (hasToSet) dispatch(setCampaignRecipients(data.recipients));

        return validateRecipientsForm(validations?.[form]?.[languageCode], data);
      case 'translations':
        if (hasToSet) dispatch(setTranslations(data.translations));

        return validateTranslationsForm(data, languageCode);
      default:
        break;
    }
  };

  const validateForm = async (form, languageCode) => {
    let response = null;
    const shouldUpdateTranslations = campaignInfo?.activeLanguages?.length > 1 && !readOnly && form === 'translations';

    if (form === 'affected-vehicles') {
      return validateAffectedVehicles();
    }

    if (campaign[getAttribute(form)]?.id && !shouldUpdateTranslations) {
      newStateCampaign = { ...newStateCampaign, [getAttribute(form)]: campaign[getAttribute(form)] };

      return validateForms(form, newStateCampaign, languageCode);
    }

    if (form === 'translations') {
      response = await campaignServices.getTranslations(id);
    } else {
      response = await campaignServices.getCampaignRetry(id, form, version, campaignInfo?.type, campaignInfo?.creationDate);
    }
    newStateCampaign = { ...newStateCampaign, [getAttribute(form)]: response };

    return validateForms(form, newStateCampaign, languageCode, true);
  };

  const validateTab = async (form, acc, languageCode) => ({
    ...acc.validations,
    [getAttribute(form)]: {
      ...acc?.validations?.[getAttribute(form)],
      [languageCode]: await validateForm(form, languageCode),
    },
  });

  const validateTabs = async (languageCode, oldValidations) => {
    const tabs = ['description', 'classification', 'affected-hardware', 'affected-software', 'affected-vehicles',
      'recipients'];
    const translationTab = campaignInfo.status === statusCampaign.IN_CREATION ? ['translations'] : [];
    const tabsToValidate = languageCode === campaignInfo.defaultLanguage ? tabs : translationTab;

    const newState = await tabsToValidate
      .reduce((previousPromise, form) => previousPromise
        .then((acc) => validateTab(form, acc, languageCode)
          .then((newValidations) => ({ ...acc, ...newValidations }))), Promise.resolve({ ...oldValidations }));

    return newState;
  };

  const validateLanguages = (newState) => campaignInfo?.activeLanguages.map(({ languageCode }) => {
    const { translations, ...othersTabs } = newState;
    const translationTab = campaignInfo?.status === statusCampaign?.IN_CREATION ? { translations } : {};
    const tabsToValidate = campaignInfo?.defaultLanguage === languageCode ? othersTabs : translationTab;

    const isValid = Object.keys(tabsToValidate).every((currentTab) => !newState[currentTab]?.[languageCode]?.invalid);

    return { language: languageCode, isValid };
  });

  const validateOneTwoEyes = (newState) => {
    if (newState.recipients?.[campaignInfo.defaultLanguage]?.fourEyesInvalid) addToast(t('alert.fourEyesInvalid'), 'Warning');
  };

  const showMissingMandatoryFieldBanner = (newState) => {
    const tabs = ['description', 'classification', SOFTWARE.AFFECTED_SOFTWARE, 'affectedVehicles',
      'translations'];
    const translationLang = campaignInfo?.defaultLanguage?.toUpperCase() === 'EN' ? 'DE' : 'EN';
    const someMissingFieldInTab = tabs.some((current) => newState?.[current]?.[current === 'translations' ? translationLang : campaignInfo.defaultLanguage]?.invalid);
    const someMissingFieldInRecipients = newState?.recipients?.[campaignInfo.defaultLanguage]?.someEmptyField;
    const incorrectRecipientsOrder = newState?.recipients?.[campaignInfo.defaultLanguage]?.incorrectOrder;

    if (incorrectRecipientsOrder) {
      addToast(t('alert.incorrectRecipientsOrder'), 'Warning');
    }

    if (someMissingFieldInTab || someMissingFieldInRecipients) {
      addToast(t('alert.mandatoryFieldsEmpty'), 'Warning', errorTypes.mandatory_fields_empty);
    }
  };

  const validateCampaign = async () => {
    const newState = await campaignInfo?.activeLanguages
      .sort((a) => (a.languageCode === campaignInfo?.defaultLanguage ? -1 : 1))// fix load all tables before validate translation tab
      .reduce((previousPromise, { languageCode }) => previousPromise
        .then((acc) => validateTabs(languageCode, acc)
          .then((newValidations) => ({ ...acc, ...newValidations }))), Promise.resolve({ ...validations }));
    const missingE3QMBPlantApprover = isVansBeforeFlowRemoval(campaignInfo?.type, campaignInfo?.creationDate)
      && Object.values(newState?.recipients?.[campaignInfo?.defaultLanguage]?.e3QMBApprover).some((approver) => approver?.errors?.required);
    if (missingE3QMBPlantApprover) addToast(t('validations.missingE3QMBApprover'), 'Warning');

    const languagesValidations = validateLanguages(newState);
    validateOneTwoEyes(newState);
    showMissingMandatoryFieldBanner(newState);

    if (!newState.active) newState.active = {};
    newState.active.all = languagesValidations.some(({ isValid }) => !isValid);
    dispatch(setCampaignHelp({ ...campaignHelp, invalidLanguages: languagesValidations, validations: newState }));
    dispatch(setValidations(newState));

    return languagesValidations?.every(({ isValid }) => isValid);
  };

  return validateCampaign;
};

export default useCampaignValidate;
